import React from "react";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Checkbox,
} from "@mui/material";
import PlayerHeadshot from "src/components/PlayerHeadshot";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SOCCER_POSITIONS } from "src/utils/scoreablesDashboardUtils";

const TeamPlayersTable = ({
  teamLabel,
  teamName,
  teamFeedId,
  scoreables,
  teamStats,
  sortData,
  editingStat,
  setEditingStat,
  editedStats,
  setEditedStats,
  sortKey,
}) => {
  console.log("Rendering TeamPlayersTable for team:", teamName, teamStats);

  const renderStatCell = (player, playerStats, statKey) => {
    const key = `${player.externalRef}-${statKey}`;
    const isEditing = editingStat?.key === key;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {isEditing ? (
          <TextField
            size="small"
            type="number"
            value={editedStats[key] ?? playerStats[statKey] ?? 0}
            onChange={(e) =>
              setEditedStats((prev) => ({
                ...prev,
                [key]: e.target.value,
              }))
            }
            autoFocus
            onBlur={() => setEditingStat(null)}
            sx={{ width: 50 }}
          />
        ) : (
          <>
            <Typography sx={{ lineHeight: "18px" }}>
              {editedStats[key] ?? playerStats[statKey] ?? 0}
            </Typography>
            <img
              src="/assets/icons/EditIcon.svg"
              alt="Edit"
              style={{ width: 18, height: 18, cursor: "pointer" }}
              onClick={() => setEditingStat({ key })}
            />
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 6, mx: 6, mb: 2 }}>
      <Box>
        <Grid item xs={4}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: 4,
              mb: 4,
              gap: 2,
            }}
          >
            <PlayerHeadshot
              row={{ externalRef: teamFeedId }}
              imageHash={teamFeedId}
            />
            <Typography
              sx={{ color: "#6A707E", fontSize: "22px", fontWeight: 700 }}
            >
              {teamName}
            </Typography>
            <Typography
              sx={{ color: "#6A707E", fontSize: "16px", fontWeight: 600 }}
            >
              {teamLabel}
            </Typography>
          </Box>
        </Grid>
      </Box>

      <TableContainer>
        <Table sx={{ borderCollapse: "separate", borderSpacing: "0 12px" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#464A53", fontWeight: 600 }}></TableCell>
              <TableCell sx={{ color: "#464A53", fontWeight: 600 }}>
                Player ID
              </TableCell>

              <TableCell sx={{ borderBottom: "none", color: "#464A53" }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  Name
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Button
                      size="small"
                      sx={{ minWidth: 0, padding: 0 }}
                      onClick={() => sortData("displayName", sortKey)}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      sx={{ minWidth: 0, padding: 0 }}
                      onClick={() => sortData("displayName", sortKey)}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </Button>
                  </Box>
                </Box>
              </TableCell>

              <TableCell sx={{ borderBottom: "none", color: "#464A53" }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  Pos
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <Button
                      size="small"
                      sx={{ minWidth: 0, padding: 0 }}
                      onClick={() => sortData("position", sortKey)}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </Button>
                    <Button
                      size="small"
                      sx={{ minWidth: 0, padding: 0 }}
                      onClick={() => sortData("position", sortKey)}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </Button>
                  </Box>
                </Box>
              </TableCell>

              {["G", "A", "SOG", "T", "PI"].map((label) => (
                <TableCell
                  key={label}
                  sx={{ color: "#464A53", fontWeight: 600 }}
                  align="center"
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {scoreables.map((player) => {
              const playerStats =
                teamStats?.Players?.find(
                  (p) => p.PlayerFeedId === player.externalRef
                ) || {};
              const isChecked = Boolean(playerStats.PlayerFeedId);

              return (
                <TableRow
                  key={player.scoreableId}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: 2,
                    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.05)",
                    overflow: "hidden",
                    height: 60,
                    "& td": {
                      borderBottom: "none",
                      paddingY: 1,
                      color: "#6A707E",
                      fontSize: "16px",
                      fontWeight: 500,
                    },
                  }}
                >
                  <TableCell align="center">
                    <Checkbox checked={isChecked} />
                  </TableCell>
                  <TableCell>{player.externalRef}</TableCell>
                  <TableCell>
                    {player.firstname && player.lastname
                      ? `${player.firstname[0]}. ${player.lastname}`
                      : player.knownname || player.shortname || "—"}
                  </TableCell>

                  <TableCell>
                    {SOCCER_POSITIONS.find(
                      (pos) => pos.value === player.position
                    )?.label || ""}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatCell(player, playerStats, "Goals")}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatCell(player, playerStats, "Assist")}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatCell(player, playerStats, "ShotsOnGoal")}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatCell(player, playerStats, "Tackles")}
                  </TableCell>
                  <TableCell align="center">
                    {renderStatCell(player, playerStats, "PassInterceptions")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TeamPlayersTable;
